import { useEffect } from 'react'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'
import DashboardLayout from '../../components/layout/DashboardLayout'
import OnboardingModal from '../../components/onboarding/OnboardingModal'
import api, { authApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { useState } from 'react'

const legacyPageToPath = {
  Project: '/dashboard/project',
  Siwes: '/dashboard/siwes',
  WordEditor: '/dashboard/editor',
  TopicIdeas: '/dashboard/topic-ideas',
  Payments: '/dashboard/payments',
  Referrals: '/dashboard/referrals',
  Usage: '/dashboard/usage',
  GetinTouch: '/dashboard/get-in-touch',
  Account: '/dashboard/account'
}

const DashboardShell = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { logout } = useAuth()

  const [me, setMe] = useState(null)
  const [onboardingOpen, setOnboardingOpen] = useState(false)

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const refreshMe = async () => {
    try {
      const meRes = await authApi.me()
      const profile = meRes?.data?.data || {}
      setMe(profile)
      setOnboardingOpen(!!profile?.onboardingDue)
    } catch {
      setMe(null)
      setOnboardingOpen(false)
    }
  }

  useEffect(() => {
    void refreshMe()
  }, [])

  // Backwards compatible support: /dashboard?page=TopicIdeas etc
  useEffect(() => {
    const requestedPage = searchParams.get('page')
    if (!requestedPage) return

    const nextPath = legacyPageToPath[requestedPage]
    if (!nextPath) return

    const projectId = searchParams.get('projectId')
    const nextSearch = new URLSearchParams()
    if (projectId) nextSearch.set('projectId', projectId)

    setSearchParams(nextSearch, { replace: true })
    navigate(`${nextPath}${nextSearch.toString() ? `?${nextSearch.toString()}` : ''}`, { replace: true })
  }, [navigate, searchParams, setSearchParams])

  // Payment verification redirect from flutterwave callback
  useEffect(() => {
    const txRef = searchParams.get('tx_ref')
    const paymentVerify = searchParams.get('payment')
    const transactionId = searchParams.get('transaction_id')
    if (paymentVerify !== 'verify' || !txRef) return

    const params = { tx_ref: txRef }
    if (transactionId) params.transaction_id = transactionId

    api
      .get('/payments/verify', { params })
      .then((verifyRes) => {
        const payment = verifyRes?.data?.data || {}
        const paidProjectId = payment?.project
        const productType = payment?.productType
        const generationType = productType === 'PROPOSAL_GENERATION'
          ? 'PROPOSAL'
          : productType === 'SIWES_REPORT_GENERATION'
            ? 'SIWES_REPORT'
            : 'DOCUMENTATION'

        const nextUrl =
          productType === 'IDEA_GENERATION_PACK'
            ? '/dashboard/topic-ideas?paymentSuccess=1'
            : productType === 'SIWES_REPORT_GENERATION' && !paidProjectId
              ? '/dashboard/siwes?paymentSuccess=1'
            : paidProjectId
              ? `/dashboard/project?projectId=${encodeURIComponent(String(paidProjectId))}&paymentSuccess=1&generationType=${encodeURIComponent(generationType)}`
              : '/dashboard/project'

        setSearchParams({}, { replace: true })
        navigate(nextUrl, { replace: true })
      })
      .catch(() => {
        setSearchParams({}, { replace: true })
        navigate('/dashboard/project', { replace: true })
      })
  }, [navigate, searchParams, setSearchParams])

  return (
    <div>
      <OnboardingModal
        open={onboardingOpen}
        initialProfile={me}
        onClose={() => setOnboardingOpen(false)}
        onRefetch={refreshMe}
      />

      <DashboardLayout onLogout={handleLogout} user={me}>
        <div className="transition-all duration-300">
          <Outlet />
        </div>
      </DashboardLayout>
    </div>
  )
}

export default DashboardShell

